import React, { useMemo, useState, useContext } from "react";
import { Table, Tag, Tooltip, Button, Input } from "antd";

import { FileText, RefreshCw } from "lucide-react";
import AppContext from '../../../../context/Context';
import { formatTimestamp } from "../utils";

const CumulativeSignalLogs = ({
	allSignalLogs,
	allLogsLoading,
	fetchAllSignalLogs
}) => {
	const {
		config: { isDark },
	} = useContext(AppContext);
	const [cumulativeLogsSearchText, setCumulativeLogsSearchText] = useState("");



	const filteredCumulativeLogs = useMemo(() => {
		if (!cumulativeLogsSearchText.trim()) return allSignalLogs;
		const searchLower = cumulativeLogsSearchText.toLowerCase().trim();
		return (allSignalLogs || []).filter(log => {
			const signalName = (log.signalName || '').toLowerCase();
			return signalName.includes(searchLower);
		});
	}, [allSignalLogs, cumulativeLogsSearchText]);

	return (
		<div>
			<div className="qt-algo-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<FileText size={20} />
					<h3>Cumulative Signal Logs</h3>
				</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<Input
						placeholder="Search signal names..."
						value={cumulativeLogsSearchText}
						onChange={(e) => setCumulativeLogsSearchText(e.target.value)}
						allowClear
						style={{ width: 200 }}
						size="small"
					/>
					<Tooltip title="Refresh logs">
						<Button
							icon={<RefreshCw size={16} />}
							size="small"
							onClick={fetchAllSignalLogs}
							loading={allLogsLoading}
						/>
					</Tooltip>
				</div>
			</div>
			<Table
				dataSource={filteredCumulativeLogs}
				loading={allLogsLoading}
				rowKey={(record, index) => `all-log-${index}`}
				size="small"
				scroll={{ y: 600, x: 'max-content' }}
				pagination={false}
				columns={[
					{
						title: 'TIMESTAMP',
						dataIndex: 'tsMicros',
						key: 'timestamp',
						align: 'left',
						sorter: (a, b) => (a.tsMicros || 0) - (b.tsMicros || 0),
						render: (tsMicros) => <span style={{ fontSize: '12px' }}>{formatTimestamp(tsMicros)}</span>,
						width: 120,
					},
                    {
						title: 'LOG TYPE',
						dataIndex: 'color',
						key: 'logType',
						align: 'center',
						width: 100,
						render: (color) => {
							let tagColor = 'blue';
							let label = 'INFO';
							switch (color?.toLowerCase()) {
								case 'red':
									tagColor = 'error';
									label = 'ERROR';
									break;
								case 'yellow':
								case 'orange':
									tagColor = 'warning';
									label = 'WARNING';
									break;
								case 'green':
									tagColor = 'success';
									label = 'SUCCESS';
									break;
								default:
									tagColor = 'blue';
									label = 'INFO';
									break;
							}
							return <Tag color={tagColor}>{label}</Tag>;
						},
					},
					{
						title: 'SIGNAL NAME',
						dataIndex: 'signalName',
						key: 'signalName',
						align: 'left',
						sorter: (a, b) => {
							const aName = (a.signalName || '').toUpperCase();
							const bName = (b.signalName || '').toUpperCase();
							return aName.localeCompare(bName);
						},
						render: (signalName) => {
							if (!signalName) {
								return <Tag color="yellow">N/A</Tag>;
							}
							return <Tag color="blue">{signalName}</Tag>;
						},
						width: 150,
					},

					{
						title: 'MESSAGE',
						dataIndex: 'msg',
						key: 'message',
						sorter: (a, b) => {
							const aMsg = ((a.msg || '') + ' ' + (a.description || '')).toUpperCase();
							const bMsg = ((b.msg || '') + ' ' + (b.description || '')).toUpperCase();
							return aMsg.localeCompare(bMsg);
						},
						render: (msg, record) => {
							const description = record.description || '';
							return (
								<span style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', textAlign: 'left' }}>
									{msg || '-'}
									{description && (
										<span style={{ fontSize: '11px', color: isDark ? '#C6C6C6' : '#666', fontStyle: 'italic', textAlign: 'left' }}>
											{description}
										</span>
									)}
								</span>
							);
						},
						ellipsis: true,
					},
				]}
				locale={{ emptyText: 'No logs available' }}
			/>
		</div>
	);
};

export default CumulativeSignalLogs;
